import React from "react";
import SummaryWrapper from "../../../../IpEMR/InspectionSummaries/SummaryWrapper";
import { checkEmpty } from "../../../../../helpers/utility";
import {
  activePassiveOptions,
  cardioCongenitalOptions,
  cardioPastMedicalOptions,
  neuroChiefComplaintOptions,
  neuroDiagnosisOptions,
  neuroHistoryOptions,
  opExaminationOptions,
  spinalOptions,
  strokeHeadInjuryOptions,
  habitOptions,
} from "../OpPhysioOptions";
import { getExerciseSummary } from "../../../../IpEMR/InspectionSummaries/constructTextFromValues";
import {
  getLabel,
  getLabelScalesTags,
  getLabelTag,
  getLabelTagInput,
  getLabelTagsArray,
  getLabelTagsWithDescription,
  getLabelTagsWithDynamicInputs,
  getLabelTagYesNO,
} from "../../../../IpEMR/InspectionSummaries/constructTextFromValues";
import {
  alcoholOptions,
  smokingOptions,
} from "../../../../IpEMR/Options/historyOptions";
import {
  asiaImpairmentGradeOptions,
  daysUptoFifteenOptions,
  maScaleOptions,
  mmtScaleOptions,
  nihssOptions,
  vcgScaleOptions,
  weeksStartingFourOptions,
  weeksStartingTwoOptions,
  neurologicalOptions,
} from "../../../../IpEMR/Options/physioOptions";
import { reflexes } from "../../../../IpEMR/Options/systemicExamOptions";
import { barthelIndexOptions } from "../../../../IpEMR/Options/functionalAsessmentOptions";
import { normalizeYesNoStored } from "../../../../IpEMR/Options/commonOptions";
import { yesNoOptions } from "../../../../../constants/defaultKeys";

const NeuroSummary = ({ formData }) => {
  const constructNeuroSummaryItems = () => {
    const getReflexesSummary = (data) => {
      const reflexOptions = reflexes
        .map((reflex) => {
          const leftValue = data?.[reflex.value + "_left"];
          const rightValue = data?.[reflex.value + "_right"];

          if (leftValue || rightValue) {
            let labelCreated = `${reflex.label} Left: ${leftValue}, Right: ${rightValue}`;
            const node = getLabel(labelCreated);
            return node
              ? React.cloneElement(node, { key: reflex.value })
              : null;
          }
          return null;
        })
        .filter(Boolean);

      return reflexOptions.length === 0 ? null : reflexOptions;
    };
    const getRomSummary = (list, data) => {
      const enteredOptions = list.reduce((acc, item) => {
        const filtered = Object.entries(data).filter(([key, value]) =>
          key.endsWith(item)
        );
        return [...acc, ...filtered];
      }, []);

      return enteredOptions?.length === 0
        ? null
        : enteredOptions?.map(([key, value]) => {
            const node = getLabelTagInput(
              key
                .split("_")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" "),
              value
            );
            return node ? React.cloneElement(node, { key }) : null;
          });
    };
    const neuroSummaryItems = [];
    const pushSummaryItem = (key, label, children) => {
      neuroSummaryItems.push({ key, label, children });
    };

    if (checkEmpty(formData?.chief_complaint)) {
      pushSummaryItem(
        "1",
        "Chief Complaints",
        <>
          {getLabelTagsArray(
            neuroChiefComplaintOptions,
            formData?.chief_complaint?.chief_complaints
          )}
          {getLabelTagInput("Comments", formData?.chief_complaint?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.history)) {
      const pastMedicalHistoryValues = formData?.history?.past_medical_history;
      const showPastMedicalOthersRemarks =
        Array.isArray(pastMedicalHistoryValues) &&
        pastMedicalHistoryValues.includes("past_medical_others");

      const congenitalDisordersValues = formData?.history?.congenital_disorders;
      const showCongenitalOtherRemarks =
        Array.isArray(congenitalDisordersValues) &&
        congenitalDisordersValues.includes("cardio_congenital_options");

      pushSummaryItem(
        "2",
        "History",
        <>
          {checkEmpty(formData?.history?.history) &&
            getLabelTagInput(
              "History",
              getLabelTagsArray(neuroHistoryOptions, formData?.history?.history)
            )}

          {(checkEmpty(pastMedicalHistoryValues) ||
            (showPastMedicalOthersRemarks &&
              checkEmpty(formData?.history?.past_medical_comments))) &&
            getLabelTagInput(
              "Past Medical History",
              <>
                {getLabelTagsArray(
                  cardioPastMedicalOptions,
                  pastMedicalHistoryValues
                )}
                {showPastMedicalOthersRemarks &&
                  formData?.history?.past_medical_comments &&
                  formData.history.past_medical_comments
                    .map((item, idx) => {
                      const node = getLabelTagInput(
                        `Remarks ${idx + 1}`,
                        item.remarks
                      );
                      const k = item?.id ?? item?.remarks ?? idx;
                      return node ? React.cloneElement(node, { key: k }) : null;
                    })
                    .filter(Boolean)}
              </>
            )}
          {(checkEmpty(congenitalDisordersValues) ||
            checkEmpty(formData?.history?.congenital_disorders_comments) ||
            (showCongenitalOtherRemarks &&
              checkEmpty(formData?.history?.congenital_disorders_other))) &&
            getLabelTagInput(
              "Congenital Disorders",
              <>
                {getLabelTagsArray(
                  cardioCongenitalOptions,
                  congenitalDisordersValues
                )}

                {showCongenitalOtherRemarks &&
                  Array.isArray(formData?.history?.congenital_disorders_other) &&
                  formData.history.congenital_disorders_other
                    .map((item, idx) => {
                      const node = getLabelTagInput(
                        `Remarks ${idx + 1}`,
                        item.remarks
                      );
                      const k = item?.id ?? item?.remarks ?? idx;
                      return node ? React.cloneElement(node, { key: k }) : null;
                    })
                    .filter(Boolean)}

                {formData?.history?.congenital_disorders_comments &&
                  getLabelTagInput(
                    "Comments",
                    formData?.history?.congenital_disorders_comments
                  )}
              </>
            )}

          {formData?.history?.past_surgical_history &&
            getLabelTagInput(
              "Past Surgical History",
              <>
                {formData.history.past_surgical_history === "yes"
                  ? "Yes"
                  : "No"}
                {(formData?.history?.past_surgical_comments ||
                  formData?.history?.past_surgical_history_comments) &&
                  getLabelTagInput(
                    "Comments",
                    formData?.history?.past_surgical_comments ??
                      formData?.history?.past_surgical_history_comments
                  )}
              </>
            )}

          {formData?.history?.smoking &&
            getLabelTagInput(
              "Smoking",
              getLabelTag(smokingOptions, formData?.history?.smoking)
            )}

          {formData?.history?.alcohol &&
            getLabelTagInput(
              "Alcohol",
              getLabelTag(alcoholOptions, formData?.history?.alcohol)
            )}

          {formData?.history?.life_style &&
            getLabelTagInput(
              "Life Style",
              getLabelTag(habitOptions, formData?.history?.life_style)
            )}
          {formData?.history?.habits_comments &&
            getLabelTagInput("Comments", formData?.history?.habits_comments)}
        </>
      );
    }

    if (checkEmpty(formData?.examination)) {
      pushSummaryItem(
        "3",
        "Examination",
        <>
          {getLabelTagsWithDescription(
            opExaminationOptions,
            formData?.examination
          )}
          {getLabelTagInput("Pain level", formData?.examination?.pain)}
          {getLabelTagInput(
            "Interpretation",
            formData?.examination?.pain_interpretation
          )}
          {getLabelTagInput(
            "Comments",
            formData?.examination?.examination_comments
          )}
        </>
      );
    }

    if (checkEmpty(formData?.gcs)) {
      pushSummaryItem(
        "4",
        "GCS",
        <>
          {getLabelTagInput("Score", formData?.gcs?.score)}
          {getLabelTagInput("Interpretation", formData?.gcs?.interpretation)}
          {getLabelTagInput("Comments", formData?.gcs?.comments)}
          {/* {getLabel(
            `E${formData?.gcs?.eye_opening_response} V${formData?.gcs?.verbal_response} M${formData?.gcs?.motor_response}`
          )} */}
        </>
      );
    }

    if (checkEmpty(formData?.ras_scale)) {
      pushSummaryItem(
        "5",
        "RASS",
        <>
          {getLabelTagInput("Score", formData?.ras_scale?.rass_scale)}
          {getLabelTagInput(
            "Interpretation",
            formData?.ras_scale?.interpretation
          )}
          {getLabelTagInput("Comments", formData?.ras_scale?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.mmt)) {
      pushSummaryItem(
        "6",
        "MMT",
        <>
          {" "}
          {getLabelTagInput(
            "Left Lower Limb",
            getLabelTag(mmtScaleOptions, formData?.mmt?.lower_left_limb)
          )}
          {getLabelTagInput(
            "Right Lower Limb",
            getLabelTag(mmtScaleOptions, formData?.mmt?.lower_right_limb)
          )}
          {getLabelTagInput(
            "Left Upper Limb",
            getLabelTag(mmtScaleOptions, formData?.mmt?.upper_left_limb)
          )}
          {getLabelTagInput(
            "Right Upper Limb",
            getLabelTag(mmtScaleOptions, formData?.mmt?.upper_right_limb)
          )}
          {formData?.mmt?.comments &&
            getLabelTagInput("Comments", formData?.mmt?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.mas)) {
      pushSummaryItem(
        "7",
        "MAS",
        <>
          {getLabelTagInput(
            "Left Lower Limb",
            getLabelTag(maScaleOptions, formData?.mas?.lower_left_limb)
          )}
          {getLabelTagInput(
            "Right Lower Limb",
            getLabelTag(maScaleOptions, formData?.mas?.lower_right_limb)
          )}
          {getLabelTagInput(
            "Left Upper Limb",
            getLabelTag(maScaleOptions, formData?.mas?.upper_left_limb)
          )}
          {getLabelTagInput(
            "Right Upper Limb",
            getLabelTag(maScaleOptions, formData?.mas?.upper_right_limb)
          )}
          {getLabelTagInput("Comments", formData?.mas?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.vcg)) {
      pushSummaryItem(
        "8",
        "VCG",
        <>
          {getLabelTagInput(
            "Left Lower Limb",
            getLabelTag(vcgScaleOptions, formData?.vcg?.lower_left_limb)
          )}
          {getLabelTagInput(
            "Right Lower Limb",
            getLabelTag(vcgScaleOptions, formData?.vcg?.lower_right_limb)
          )}
          {getLabelTagInput(
            "Left Upper Limb",
            getLabelTag(vcgScaleOptions, formData?.vcg?.upper_left_limb)
          )}
          {getLabelTagInput(
            "Right Upper Limb",
            getLabelTag(vcgScaleOptions, formData?.vcg?.upper_right_limb)
          )}
          {getLabelTagInput("Comments", formData?.vcg?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.moca)) {
      pushSummaryItem(
        "9",
        "MOCA",
        <>
          {getLabelTagInput("Score", formData?.moca?.score)}
          {getLabelTagInput("Interpretation", formData?.moca?.interpretation)}
          {getLabelTagInput(
            "Visual/Executive",
            formData?.moca?.visual_executive
          )}
          {getLabelTagInput("Naming", formData?.moca?.naming)}
          {getLabelTagInput("Attention", formData?.moca?.attention)}
          {getLabelTagInput("Language", formData?.moca?.language)}
          {getLabelTagInput("Abstraction", formData?.moca?.abstraction)}
          {getLabelTagInput("Delayed Recall", formData?.moca?.delayed_recall)}
          {getLabelTagInput("Orientation", formData?.moca?.orientation)}
          {getLabelTagInput("Comments", formData?.moca?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.finger_grip_test)) {
      pushSummaryItem(
        "10",
        "Finger Grip Test",
        <>
          {getLabelTagInput("Age", formData?.finger_grip_test?.age)}
          {getLabelTagInput(
            "Left Hand Grip Strength",
            formData?.finger_grip_test?.left_hand
          )}
          {getLabelTagInput(
            "Left Hand Grip Interpretation",
            formData?.finger_grip_test?.left_hand_interpretation
          )}
          {getLabelTagInput(
            "Right Hand Grip Strength",
            formData?.finger_grip_test?.right_hand
          )}
          {getLabelTagInput(
            "Right Hand Grip Interpretation",
            formData?.finger_grip_test?.right_hand_interpretation
          )}
          {getLabelTagInput("Comments", formData?.finger_grip_test?.comments)}
        </>
      );
    }
    if (checkEmpty(formData?.rom)) {
      pushSummaryItem(
        "11",
        "ROM",
        <>
          {formData?.rom?.shoulder_joint &&
            getLabelTagInput(
              "Shoulder Joint",
              getRomSummary(
                [
                  "flexion",
                  "extension",
                  "abduction",
                  "adduction",
                  "medial_rotation",
                  "lateral_rotation",
                ],
                formData?.rom?.shoulder_joint
              )
            )}
          {formData?.rom?.elbow_joint &&
            getLabelTagInput(
              "Elbow Joint",
              getRomSummary(
                ["flexion", "extension"],
                formData?.rom?.elbow_joint
              )
            )}
          {formData?.rom?.forearm_joint &&
            getLabelTagInput(
              "Forearm Joint",
              getRomSummary(
                ["supination", "pronation"],
                formData?.rom?.forearm_joint
              )
            )}
          {formData?.rom?.wrist_joint &&
            getLabelTagInput(
              "Wrist Joint",
              getRomSummary(
                ["flexion", "extension", "radial_deviation", "ulnar_deviation"],
                formData?.rom?.wrist_joint
              )
            )}
          {formData?.rom?.thumb_joint &&
            getLabelTagInput(
              "Thumb Joint",
              getRomSummary(
                ["cmc_flexion", "cmc_extension", "cmc_abduction", "ip_flexion"],
                formData?.rom?.thumb_joint
              )
            )}
          {formData?.rom?.metacarpel_joint &&
            getLabelTagInput(
              "MetaCarpel Joint",
              getRomSummary(
                ["flexion", "hyperextension", "abduction"],
                formData?.rom?.metacarpel_joint
              )
            )}
          {formData?.rom?.proximal_joint &&
            getLabelTagInput(
              "Proximal Interphalangeal Joint",
              getRomSummary(
                ["flexion", "extension"],
                formData?.rom?.proximal_joint
              )
            )}
          {formData?.rom?.disital_joint &&
            getLabelTagInput(
              "Disital Interphalangeal Joint",
              getRomSummary(
                ["flexion", "extension"],
                formData?.rom?.disital_joint
              )
            )}
          {formData?.rom?.hip_joint &&
            getLabelTagInput(
              "Hip Joint",
              getRomSummary(
                [
                  "flexion",
                  "extension",
                  "abduction",
                  "adduction",
                  "medial_rotation",
                  "lateral_rotation",
                ],
                formData?.rom?.hip_joint
              )
            )}
          {formData?.rom?.knee_joint &&
            getLabelTagInput(
              "Knee Joint",
              getRomSummary(["flexion", "extension"], formData?.rom?.knee_joint)
            )}
          {formData?.rom?.ankle_joint &&
            getLabelTagInput(
              "Ankle Joint",
              getRomSummary(
                ["dorsiflexion", "plantarflexion"],
                formData?.rom?.ankle_joint
              )
            )}
          {formData?.rom?.foot_joint &&
            getLabelTagInput(
              "Foot Joint",
              getRomSummary(
                ["inversion", "eversion"],
                formData?.rom?.foot_joint
              )
            )}
          {formData?.rom?.great_toe_joint &&
            getLabelTagInput(
              "Great Toe Joint",
              getRomSummary(
                ["flexion", "extension"],
                formData?.rom?.great_toe_joint
              )
            )}
          {formData?.rom?.inter_phalangeal_joint &&
            getLabelTagInput(
              "Inter Phalangeal Joint",
              getRomSummary(
                ["flexion", "extension"],
                formData?.rom?.inter_phalangeal_joint
              )
            )}
          {formData?.rom?.cervical_spine &&
            getLabelTagInput(
              "Cervical Spine",
              getRomSummary(
                [
                  "flexion",
                  "extension",
                  "right_rotation",
                  "left_rotation",
                  "right_lateral_rotation",
                  "left_lateral_rotation",
                ],
                formData?.rom?.cervical_spine
              )
            )}
          {formData?.rom?.thoraco_lumbar_spine &&
            getLabelTagInput(
              "Thoraco Lumbar Spine",
              getRomSummary(
                [
                  "flexion",
                  "extension",
                  "right_rotation",
                  "left_rotation",
                  "right_lateral_rotation",
                  "left_lateral_rotation",
                ],
                formData?.rom?.thoraco_lumbar_spine
              )
            )}
          {formData?.rom?.comments &&
            getLabelTagInput("Comments", formData?.rom?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.reflexes)) {
      pushSummaryItem(
        "12",
        "Reflexes",
        <> {getReflexesSummary(formData?.reflexes)}</>
      );
    }

    if (checkEmpty(formData?.coordination)) {
      pushSummaryItem(
        "13",
        "Coordination",
        <>
          {getLabelTagInput(
            "Finger-to-Nose Test",
            formData?.coordination?.finger_to_nose
          )}
          {getLabelTagInput(
            "Finger-to-Finger Test",
            formData?.coordination?.finger_to_finger
          )}
          {getLabelTagInput(
            "Adiadokokinesia or Dysdiadokokinesia",
            formData?.coordination?.adiadochokinesia_or_dysdiadokokinesia
          )}
          {getLabelTagInput(
            "Rebound Phenomena",
            formData?.coordination?.rebound_phenomenon
          )}
          {getLabelTagInput(
            "Walking Along a Straight Line",
            formData?.coordination?.walking_along_straight_line
          )}
          {getLabelTagInput(
            "Romberg's Test",
            formData?.coordination?.rombergs_test
          )}
          {getLabelTagInput(
            "Heel on Shin Test",
            formData?.coordination?.heel_to_shin ??
              formData?.coordination?.heel_on_shin
          )}
          {getLabelTagInput("Comments", formData?.coordination?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.tcms)) {
      pushSummaryItem(
        "14",
        "TCMS",
        <>
          {getLabelTagInput("Score", formData?.tcms?.score)}
          {getLabelTagInput("Interpretation", formData?.tcms?.interpretation)}
          {getLabelTagInput(
            "Static Sitting Balance",
            formData?.tcms?.sitting_balance
          )}
          {getLabelTagInput(
            "Selective Movement Control",
            formData?.tcms?.selective_movement_control
          )}
          {getLabelTagInput(
            "Dynamic Reaching",
            formData?.tcms?.sitting_to_standing
          )}
          {getLabelTagInput("Comments", formData?.tcms?.comments)}
        </>
      );
    }

    const barthelData = formData?.barthel_index || formData?.barthel;
    if (checkEmpty(barthelData)) {
      pushSummaryItem(
        "15",
        "Barthel Index",
        <>
          {getLabelTagInput("Score", barthelData?.score)}
          {getLabelTagInput("Interpretation", barthelData?.interpretation)}
          {getLabelScalesTags(barthelIndexOptions, barthelData)}
          {getLabelTagInput("Comments", barthelData?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.asia)) {
      pushSummaryItem(
        "16",
        "ASIA",
        <>
          {getLabelTagInput(
            "Lower Extremity Motor Score",
            formData?.asia?.lower_extremity_motor_score
          )}
          {getLabelTagInput(
            "Upper Extremity Motor Score",
            formData?.asia?.upper_extremity_motor_score
          )}
          {getLabelTagInput(
            "Left Total Motor Score",
            formData?.asia?.left_total_motor_score
          )}
          {getLabelTagInput(
            "Right Total Motor Score",
            formData?.asia?.right_total_motor_score
          )}
          {getLabelTagInput(
            "Left Total Sensory Score",
            formData?.asia?.left_total_sensory_score
          )}
          {getLabelTagInput(
            "Right Total Sensory Score",
            formData?.asia?.right_total_sensory_score
          )}
          {getLabelTagInput("Total LT", formData?.asia?.total_lt)}
          {getLabelTagInput("Total PP", formData?.asia?.total_pp)}
          {getLabelTagInput(
            "DAP",
            getLabelTag(yesNoOptions, normalizeYesNoStored(formData?.asia?.dap))
          )}
          {getLabelTagInput(
            "VAC",
            getLabelTag(yesNoOptions, normalizeYesNoStored(formData?.asia?.vac))
          )}
          {getLabelTagInput("Status", formData?.asia?.interpretation)}
          {getLabelTagInput(
            "Sensory Left",
            getLabelTag(neurologicalOptions, formData?.asia?.sensory_left)
          )}
          {getLabelTagInput(
            "Sensory Right",
            getLabelTag(neurologicalOptions, formData?.asia?.sensory_right)
          )}
          {getLabelTagInput(
            "Motor Left",
            getLabelTag(neurologicalOptions, formData?.asia?.motor_left)
          )}
          {getLabelTagInput(
            "Motor Right",
            getLabelTag(neurologicalOptions, formData?.asia?.motor_right)
          )}
          {getLabelTagInput(
            "Neurological Level Injury",
            getLabelTag(
              neurologicalOptions,
              formData?.asia?.nerological_level_of_injury
            )
          )}
          {getLabelTagInput(
            "ASIA Impairment Scale Grade",
            getLabelTag(
              asiaImpairmentGradeOptions,
              formData?.asia?.asia_impairment_scale
            )
          )}
          {getLabelTagInput(
            "Zone of Partial Preservation",
            getLabelTag(
              neurologicalOptions,
              formData?.asia?.zone_of_partial_preservation
            )
          )}
          {getLabelTagInput("Comments", formData?.asia?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.nihss)) {
      pushSummaryItem(
        "17",
        "NIHSS",
        <>
          {getLabelTagInput("Score", formData?.nihss?.score)}
          {getLabelTagInput("Interpretation", formData?.nihss?.interpretation)}
          {getLabelScalesTags(nihssOptions, formData?.nihss)}
          {getLabelTagInput("Comments", formData?.nihss?.comments)}
        </>
      );
    }

    if (checkEmpty(formData?.functional_diagnosis)) {
      const functionalDiagnosisValues = formData?.functional_diagnosis?.diagnosis;
      const showFunctionalDiagnosisOthers =
        Array.isArray(functionalDiagnosisValues) &&
        functionalDiagnosisValues.includes("others");
      pushSummaryItem(
        "18",
        "Functional Diagnosis",
        <>
          {getLabelTagsArray(
            neuroDiagnosisOptions,
            functionalDiagnosisValues
          )}
          {showFunctionalDiagnosisOthers &&
            getLabelTagsWithDynamicInputs(
              formData?.functional_diagnosis?.diagnosis_others
            )}
          {getLabelTagInput(
            "Stroke",
            getLabelTag(
              strokeHeadInjuryOptions,
              formData?.functional_diagnosis?.stroke
            )
          )}
          {getLabelTagInput(
            "Head Injury",
            getLabelTag(
              strokeHeadInjuryOptions,
              formData?.functional_diagnosis?.head_injury
            )
          )}
          {getLabelTagInput(
            "Spinal Cord Injury",
            getLabelTag(
              spinalOptions,
              formData?.functional_diagnosis?.spinal_cord_injury
            )
          )}
          {getLabelTagInput(
            "Comments",
            formData?.functional_diagnosis?.diagnosis_comments
          )}
        </>
      );
    }

    if (checkEmpty(formData?.therapy)) {
      const therapyOthersRemarks = Array.isArray(formData?.therapy?.others)
        ? formData.therapy.others.map((o) => o?.remarks).filter(Boolean)
        : [];
      pushSummaryItem(
        "19",
        "Therapy",
        <>
          {getLabelTagInput(
            "Stroke Rehab",
            getLabelTag(
              weeksStartingFourOptions,
              formData?.therapy?.stroke_rehab
            )
          )}
          {getLabelTagInput(
            "Brustromme Approach",
            formData?.therapy?.brustromme_approach
          )}
          {getLabelTagInput("MRP Approach", formData?.therapy?.mrp_approach)}
          {getLabelTagInput("NDT Approach", formData?.therapy?.ndt_approach)}
          {getLabelTagInput("CIMT Approach", formData?.therapy?.cimt_approach)}
          {getLabelTagInput(
            "VOJTA Approach",
            formData?.therapy?.vojta_approach
          )}
          {getLabelTagInput("PNF Approach", formData?.therapy?.pnf_approach)}
          {getLabelTagInput(
            "Electic Approach",
            formData?.therapy?.electic_approach
          )}
          {getLabelTagInput(
            "ROODS Approach",
            formData?.therapy?.roods_approach
          )}
          {getLabelTagInput(
            "Speech Therapy",
            formData?.therapy?.speech_therapy
          )}
          {getLabelTagInput(
            "Electical Muscle Stimulation",
            formData?.therapy?.electrical_muscle_stimulation
          )}
          {getLabelTagInput(
            "Mile Stone Training",
            formData?.therapy?.mile_stone_training
          )}
          {getLabelTagInput(
            "Cognitive Rehab",
            formData?.therapy?.cognitive_rehab
          )}
          {getLabelTagInput(
            "Neuro Conditioning Training",
            formData?.therapy?.neuro_conditioning_training
          )}
          {getLabelTagInput(
            "Sensory Integration",
            formData?.therapy?.sensory_integration
          )}
          {getLabelTagInput("ADL Training", formData?.therapy?.adl_training)}
          {getLabelTagInput(
            "Wheel Chair Training",
            formData?.therapy?.wheel_chair_training
          )}
          {getLabelTagInput(
            "Ultrasound",
            getLabelTag(daysUptoFifteenOptions, formData?.therapy?.ultrasound)
          )}
          {getLabelTagInput(
            "Splinting/ Bracing",
            getLabelTag(weeksStartingTwoOptions, formData?.therapy?.splinting)
          )}
          {getLabelTagInput(
            "Postural Correction",
            formData?.therapy?.postural_correction
          )}
          {getLabelTagInput("Robotics", formData?.therapy?.robotics)}
          {getLabelTagInput("Gait Training", formData?.therapy?.gait_training)}
          {therapyOthersRemarks.length > 0 &&
            getLabelTagInput("Others", therapyOthersRemarks.join(", "))}
          {getLabelTagInput("Therapy Comments", formData?.therapy?.description)}
        </>
      );
    }
    const exerciseList = Array.isArray(formData?.exercise)
      ? formData.exercise
      : Object.values(formData?.exercise || {});

    if (exerciseList.length > 0) {
      pushSummaryItem("20", "Exercise", getExerciseSummary(exerciseList));
    }

    if (checkEmpty(formData?.review_notes)) {
      pushSummaryItem(
        "21",
        "Review Notes",
        <>
          {getLabelTagInput(
            "Progress Notes",
            formData?.review_notes?.progress_notes
          )}
          {getLabelTagInput(
            "Review Notes",
            formData?.review_notes?.review_notes
          )}
        </>
      );
    }

    return neuroSummaryItems;
  };

  const neuroSummaryItems = constructNeuroSummaryItems();
  return <SummaryWrapper items={neuroSummaryItems} />;
};

export default NeuroSummary;
